import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "@mantine/form";
import { TextInput, PasswordInput, Button, Stack, Checkbox, Alert, Paper, PinInput, Center, Group, Text } from "@mantine/core";
import { STORAGE_TOKEN_KEY, useAuth } from "../../contexts/AuthContext";
import { useToastNotification } from "../../hooks/useToastNotification";
import { IconMail, IconLock } from "@tabler/icons-react";
import { HttpCodes, Logo, AppName } from "../../lib/data";
import "./styles.scss";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../lib/usePerformRequest";
import { NonPaginatedResponse } from "../../types";
import { IUserProps } from "../../types/User";

export default function Login() {
   const navigate = useNavigate();
   const [isLoading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const { reloadUser } = useAuth();
   const { success, error: showError } = useToastNotification();
   const [otpSent, setOtpSent] = useState(false);
   const [twoFactorRequired, setTwoFactorRequired] = useState(false);
   const [emailForOtp, setEmailForOtp] = useState<string>("");
   const [emailForDisplay, setEmailForDisplay] = useState<string>("");
   const [passwordFor2FA, setPasswordFor2FA] = useState<string>("");
   const [cooldownTimer, setCooldownTimer] = useState(0);
   const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
   const [isResendingOtp, setIsResendingOtp] = useState(false);
   const [isVerifying2FA, setIsVerifying2FA] = useState(false);

   const loginForm = useForm({
      initialValues: {
         email: "",
         password: "",
         rememberMe: false,
      },
      validate: {
         email: (value: string) => {
            if (!value) return "Email is required";
            return /^\S+@\S+$/.test(value) ? null : "Invalid email address";
         },
         password: (value: string) => (value.length < 6 ? "Password must be at least 6 characters" : null),
      },
   });

   const otpForm = useForm({
      initialValues: {
         otp: "",
      },
      validate: {
         otp: (value: string) => {
            if (!value) return "OTP is required";
            if (value.length !== 6) return "OTP must be 6 digits";
            return null;
         },
      },
   });

   const twoFactorForm = useForm({
      initialValues: {
         twoFactorCode: "",
      },
      validate: {
         twoFactorCode: (value: string) => {
            if (!value) return "2FA code is required";
            if (value.length !== 6) return "2FA code must be 6 digits";
            return null;
         },
      },
   });

   // Cooldown timer effect
   useEffect(() => {
      if (cooldownTimer > 0) {
         const timer = setInterval(() => {
            setCooldownTimer((prev) => Math.max(0, prev - 1));
         }, 1000);
         return () => clearInterval(timer);
      }
   }, [cooldownTimer]);

   const HandleLoginUser = async () => {
      setError(null);
      const { hasErrors } = loginForm.validate();
      if (!hasErrors) {
         setLoading(true);
         try {
            const { email, password } = loginForm.values;
            const r = await PerformRequest<NonPaginatedResponse<{
               token?: string;
               admin?: IUserProps;
               requiresTwoFactor?: boolean;
               email?: string;
               adminId?: string;
            }>>({
               method: Endpoints.GetLogin().method,
               route: Endpoints.GetLogin().route,
               data: {
                  email,
                  password,
               },
               doNotUseAuthorization: true,
            });
            if (r) {
               if (r.status === HttpCodes.Success) {
                  // Check if 2FA is required
                  if (r?.data?.data?.requiresTwoFactor) {
                     setTwoFactorRequired(true);
                     setEmailForOtp(email);
                     setEmailForDisplay(email);
                     setPasswordFor2FA(password);
                     twoFactorForm.reset();
                     success("Please enter your 2FA code");
                  } else {
                     // Login successful - token returned directly
                     const { token } = r?.data?.data || {};
                     if (token) {
                        localStorage.setItem(STORAGE_TOKEN_KEY, token);
                        // Reload user profile after saving token
                        await reloadUser();
                        success("Login successful!");
                        navigate("/dashboard");
                     } else {
                        const errorMessage = "Invalid response from server";
                        setError(errorMessage);
                        showError(errorMessage);
                     }
                  }
               } else {
                  const errorMessage = r.message || "Login failed";
                  setError(errorMessage);
                  showError(errorMessage);
               }
            }
         } catch (err: any) {
            const errorMessage = err.message || "Login failed";
            setError(errorMessage);
            showError(errorMessage);
         } finally {
            setLoading(false);
         }
      }
   };

   const HandleVerifyOtp = async () => {
      setError(null);
      const { hasErrors } = otpForm.validate();
      if (!hasErrors) {
         setIsVerifyingOtp(true);
         try {
            const { otp } = otpForm.values;
            const r = await PerformRequest<NonPaginatedResponse<{
               token: string;
               admin: IUserProps;
            }>>({
               method: Endpoints.GetVerifyLoginOtp().method,
               route: Endpoints.GetVerifyLoginOtp().route,
               data: {
                  email: emailForOtp,
                  otp,
               },
               doNotUseAuthorization: true,
            });
            if (r) {
               if (r.status === HttpCodes.Success) {
                  const { token } = r?.data?.data;
                  localStorage.setItem(STORAGE_TOKEN_KEY, token);
                  // Reload user profile after saving token
                  await reloadUser();
                  success("Login successful!");
                  navigate("/dashboard");
               } else {
                  const errorMessage = r.message || "Invalid OTP";
                  setError(errorMessage);
                  showError(errorMessage);
               }
            }
         } catch (err: any) {
            const errorMessage = err.message || "Verification failed";
            setError(errorMessage);
            showError(errorMessage);
         } finally {
            setIsVerifyingOtp(false);
         }
      }
   };

   const HandleVerifyTwoFactorCode = async () => {
      setError(null);
      const { hasErrors } = twoFactorForm.validate();
      if (!hasErrors) {
         setIsVerifying2FA(true);
         try {
            const { twoFactorCode } = twoFactorForm.values;
            const r = await PerformRequest<NonPaginatedResponse<{
               token: string;
               admin: IUserProps;
            }>>({
               method: Endpoints.GetVerifyTwoFactorLogin().method,
               route: Endpoints.GetVerifyTwoFactorLogin().route,
               data: {
                  email: emailForOtp,
                  password: passwordFor2FA,
                  twoFactorCode,
               },
               doNotUseAuthorization: true,
            });
            if (r) {
               if (r.status === HttpCodes.Success) {
                  const { token } = r?.data?.data;
                  localStorage.setItem(STORAGE_TOKEN_KEY, token);
                  await reloadUser();
                  success("Login successful!");
                  navigate("/dashboard");
               } else {
                  const errorMessage = r.message || "Invalid 2FA code";
                  setError(errorMessage);
                  showError(errorMessage);
               }
            }
         } catch (err: any) {
            const errorMessage = err.message || "Verification failed";
            setError(errorMessage);
            showError(errorMessage);
         } finally {
            setIsVerifying2FA(false);
         }
      }
   };

   const HandleResendOtp = async () => {
      if (cooldownTimer > 0) return;
      
      setError(null);
      setIsResendingOtp(true);
      try {
         const r = await PerformRequest<NonPaginatedResponse<{
            message?: string;
            email?: string;
            otp?: string;
         }>>({
            method: Endpoints.GetResendLoginOtp().method,
            route: Endpoints.GetResendLoginOtp().route,
            data: {
               email: emailForOtp,
            },
            doNotUseAuthorization: true,
         });
         if (r) {
            if (r.status === HttpCodes.Success) {
               setCooldownTimer(30); // Reset 30-second cooldown
               otpForm.setFieldValue("otp", "");
               success(r?.data?.data?.message || "OTP resent to your email");
            } else {
               const errorMessage = r.message || "Failed to resend OTP";
               setError(errorMessage);
               showError(errorMessage);
            }
         }
      } catch (err: any) {
         const errorMessage = err.message || "Failed to resend OTP";
         setError(errorMessage);
         showError(errorMessage);
      } finally {
         setIsResendingOtp(false);
      }
   };

   return (
      <div
         className="min-h-screen flex items-center justify-center"
         style={{ background: "linear-gradient(135deg, #699269 0%, #81C784 50%, #A5D6A7 100%)" }}
      >
         {/* Login Form Container */}
         <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-md mx-4"
         >
            <Paper p="xl" radius="md" shadow="xl" style={{ background: "white" }}>
               {/* Logo */}
               <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="flex justify-center mb-8"
               >
                  <img src={Logo} alt={`${AppName} Logo`} className="h-16" />
               </motion.div>

               {/* Header */}
               <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-center mb-8"
               >
                  <h1 className="text-3xl font-bold mb-2" style={{ color: "#679369" }}>
                     {otpSent ? "Verify Your Code" : twoFactorRequired ? "Enter 2FA Code" : "Admin Login"}
                  </h1>
                  <p className="text-gray-600 text-sm">
                     {otpSent
                        ? "Enter the 6-digit code sent to your email"
                        : twoFactorRequired
                        ? "Enter the 6-digit code from your authenticator app"
                        : "Access the admin dashboard"}
                  </p>
               </motion.div>

               {/* Error Alert */}
               {error && (
                  <motion.div
                     initial={{ opacity: 0, height: 0 }}
                     animate={{ opacity: 1, height: "auto" }}
                     className="mb-6"
                  >
                     <Alert color="red" title="Error">
                        {error}
                     </Alert>
                  </motion.div>
               )}

               {/* 2FA Verification Step */}
               {twoFactorRequired ? (
                  <motion.div
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                  >
                     <form
                        onSubmit={(e) => {
                           e.preventDefault();
                           HandleVerifyTwoFactorCode();
                        }}
                     >
                        <Stack gap="md">
                           <div className="text-center mb-4">
                              <Text size="lg" fw={600} c="dimmed">
                                 Enter 2FA Code
                              </Text>
                              <Text size="sm" c="dimmed" mt="xs">
                                 Enter the 6-digit code from your authenticator app
                              </Text>
                           </div>

                           <Center>
                              <PinInput
                                 length={6}
                                 size="md"
                                 {...twoFactorForm.getInputProps("twoFactorCode")}
                                 type="number"
                                 oneTimeCode
                                 autoFocus
                              />
                           </Center>

                           {twoFactorForm.errors.twoFactorCode && (
                              <Text size="sm" c="red" ta="center">
                                 {twoFactorForm.errors.twoFactorCode}
                              </Text>
                           )}

                           <Group justify="space-between" mt="md">
                              <Button
                                 variant="subtle"
                                 onClick={() => {
                                    setTwoFactorRequired(false);
                                    twoFactorForm.setFieldValue("twoFactorCode", "");
                                    setError(null);
                                 }}
                                 disabled={isVerifying2FA}
                              >
                                 Back
                              </Button>
                           </Group>

                           <Button
                              type="submit"
                              fullWidth
                              size="md"
                              onClick={HandleVerifyTwoFactorCode}
                              loading={isVerifying2FA}
                              style={{
                                 backgroundColor: "#679369",
                                 ":hover": { backgroundColor: "#558B2F" },
                              }}
                           >
                              Verify & Login
                           </Button>
                        </Stack>
                     </form>
                  </motion.div>
               ) : otpSent ? (
                  /* OTP Verification Step */
                  <motion.div
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                  >
                     <form
                        onSubmit={(e) => {
                           e.preventDefault();
                           HandleVerifyOtp();
                        }}
                     >
                        <Stack gap="md">
                           <div className="text-center mb-4">
                              <Text size="lg" fw={600} c="dimmed">
                                 Enter Verification Code
                              </Text>
                              <Text size="sm" c="dimmed" mt="xs">
                                 We've sent a 6-digit code to{" "}
                                 <strong>{emailForDisplay}</strong>
                              </Text>
                           </div>

                           <Center>
                              <PinInput
                                 length={6}
                                 size="md"
                                 {...otpForm.getInputProps("otp")}
                                 type="number"
                                 oneTimeCode
                                 autoFocus
                              />
                           </Center>

                           {otpForm.errors.otp && (
                              <Text size="sm" c="red" ta="center">
                                 {otpForm.errors.otp}
                              </Text>
                           )}

                           <Group justify="space-between" mt="md">
                              <Button
                                 variant="subtle"
                                 onClick={() => {
                                    setOtpSent(false);
                                    otpForm.setFieldValue("otp", "");
                                    setError(null);
                                 }}
                                 disabled={isVerifyingOtp}
                              >
                                 Back
                              </Button>
                              <Button
                                 onClick={HandleResendOtp}
                                 variant="subtle"
                                 disabled={cooldownTimer > 0 || isResendingOtp}
                                 loading={isResendingOtp}
                                 style={{ color: "#699269" }}
                              >
                                 {cooldownTimer > 0
                                    ? `Resend in ${cooldownTimer}s`
                                    : "Resend Code"}
                              </Button>
                           </Group>

                           <Button
                              type="submit"
                              fullWidth
                              size="md"
                              onClick={HandleVerifyOtp}
                              loading={isVerifyingOtp}
                              style={{
                                 backgroundColor: "#679369",
                                 ":hover": { backgroundColor: "#558B2F" },
                              }}
                           >
                              Verify & Login
                           </Button>
                        </Stack>
                     </form>
                  </motion.div>
               ) : (
                  /* Login Form */
                  <form
                     onSubmit={(e) => {
                        e.preventDefault();
                        HandleLoginUser();
                     }}
                  >
                  <Stack gap="md">
                     {/* Email Input */}
                     <TextInput
                        {...loginForm.getInputProps("email")}
                        label="Email"
                        placeholder="Enter your email address"
                        leftSection={<IconMail size={20} stroke={1.5} />}
                        size="md"
                     />

                     {/* Password Input */}
                     <PasswordInput
                        {...loginForm.getInputProps("password")}
                        label="Password"
                        placeholder="Enter your password"
                        leftSection={<IconLock size={20} stroke={1.5} />}
                        size="md"
                        onKeyDown={(e) => {
                           if (e.key === "Enter") {
                              e.preventDefault();
                              HandleLoginUser();
                           }
                        }}
                     />

                  {/* Remember Me */}
                  <Checkbox
                     {...loginForm.getInputProps("rememberMe", { type: "checkbox" })}
                     label="Remember Me"
                     size="sm"
                  />

                     {/* Login Button */}
                     <Button
                        type="submit"
                        fullWidth
                        size="md"
                        onClick={HandleLoginUser}
                        loading={isLoading}
                        style={{
                           backgroundColor: "#679369",
                           ":hover": { backgroundColor: "#558B2F" },
                        }}
                     >
                        Login
                     </Button>
                  </Stack>
               </form>
               )}
            </Paper>
         </motion.div>
      </div>
   );
}
